import { useState, type FormEvent } from "react";
import { useAuthActions } from "@convex-dev/auth/react";

import { Button, Field, Notice } from "../components/ui";
import { describeSignInError } from "../lib/errors";

type Step = "email" | "code";

export function SignInView({
  onRequestCode,
  onVerifyCode,
}: {
  onRequestCode: (email: string) => Promise<void>;
  onVerifyCode: (email: string, code: string) => Promise<void>;
}) {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState<"request" | "verify" | "resend" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const normalizedEmail = email.trim().toLowerCase();

  async function requestCode(kind: "request" | "resend") {
    if (busy) return;
    if (!normalizedEmail || !normalizedEmail.includes("@")) {
      setError("Enter a valid email address.");
      setStatus(null);
      return;
    }

    setBusy(kind);
    setError(null);
    setStatus(null);
    try {
      await onRequestCode(normalizedEmail);
      setEmail(normalizedEmail);
      setCode("");
      setStep("code");
      setStatus(
        kind === "resend"
          ? "A new code has been requested. Check your email."
          : "If this address can use Packful, a sign-in code has been sent.",
      );
    } catch (caught) {
      setError(describeSignInError(caught));
    } finally {
      setBusy(null);
    }
  }

  async function verifyCode() {
    if (busy) return;
    const normalizedCode = code.replace(/\D/g, "");
    if (normalizedCode.length !== 8) {
      setError("Enter the 8-digit code from your email.");
      setStatus(null);
      return;
    }

    setBusy("verify");
    setError(null);
    setStatus("Checking your code…");
    try {
      await onVerifyCode(normalizedEmail, normalizedCode);
      setStatus("Code accepted. Checking operator access…");
    } catch (caught) {
      setError(describeSignInError(caught));
      setStatus(null);
    } finally {
      setBusy(null);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void (step === "email" ? requestCode("request") : verifyCode());
  }

  function useAnotherEmail() {
    if (busy) return;
    setStep("email");
    setCode("");
    setError(null);
    setStatus(null);
  }

  return (
    <div className="sign-in">
      <p className="eyebrow">Operator console</p>
      <h1>{step === "email" ? "Sign in to Packful" : "Enter your code"}</h1>
      <p className="sign-in__intro">
        {step === "email"
          ? "Use the verified email configured for the Packful operator."
          : `We sent an 8-digit code to ${normalizedEmail}.`}
      </p>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {status ? <Notice tone="neutral">{status}</Notice> : null}
      <form className="form-stack" onSubmit={submit} noValidate>
        {step === "email" ? (
          <Field label="Operator email">
            <input
              autoComplete="email"
              autoFocus
              inputMode="email"
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@packful.app"
              type="email"
              value={email}
            />
          </Field>
        ) : (
          <Field label="8-digit code">
            <input
              autoComplete="one-time-code"
              autoFocus
              className="code-input"
              inputMode="numeric"
              maxLength={8}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
              placeholder="00000000"
              value={code}
            />
          </Field>
        )}
        <Button type="submit" busy={busy === "request" || busy === "verify"}>
          {step === "email" ? "Send sign-in code" : "Verify and continue"}
        </Button>
      </form>
      {step === "code" ? (
        <div className="sign-in__secondary">
          <Button tone="quiet" busy={busy === "resend"} onClick={() => void requestCode("resend")}>
            Resend code
          </Button>
          <Button tone="quiet" disabled={busy !== null} onClick={useAnotherEmail}>
            Use another email
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function SignIn() {
  const { signIn } = useAuthActions();
  return (
    <SignInView
      onRequestCode={async (email) => {
        await signIn("resend-otp", { email });
      }}
      onVerifyCode={async (email, code) => {
        await signIn("resend-otp", { email, code });
      }}
    />
  );
}
